// API service for communicating with the backend.
// Dev: default /api + Vite proxy (see vite.config.js) avoids CORS and wrong ports; override with VITE_API_BASE_URL.
// Prod: use VITE_API_BASE_URL at build time, or same-origin /api when the SPA and API share a host.
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.DEV
    ? '/api'
    : typeof window !== 'undefined' && window.location?.origin
      ? `${window.location.origin}/api`
      : 'http://localhost:8080/api')

// Token storage keys
const ACCESS_TOKEN_KEY = 'alchemy_access_token'
const REFRESH_TOKEN_KEY = 'alchemy_refresh_token'

// Token management functions
export const tokenStorage = {
  getAccessToken: () => localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  setTokens: (accessToken, refreshToken) => {
    if (accessToken) localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
    if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  },
  clearTokens: () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  },
}

// Attempt to refresh the access token
async function refreshAccessToken() {
  const refreshToken = tokenStorage.getRefreshToken()
  if (!refreshToken) {
    throw new Error('No refresh token available')
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })

    if (!response.ok) {
      // Refresh failed, clear tokens
      tokenStorage.clearTokens()
      throw new Error('Token refresh failed')
    }

    const data = await response.json()
    tokenStorage.setTokens(data.access_token, data.refresh_token)
    return data.access_token
  } catch (error) {
    tokenStorage.clearTokens()
    throw error
  }
}

async function fetchFileBody(id, as) {
  const url = `${API_BASE_URL}/files/${id}`
  const headers = {}
  const accessToken = tokenStorage.getAccessToken()
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`
  }

  const send = (token) => {
    const h = { ...headers }
    if (token) h.Authorization = `Bearer ${token}`
    return fetch(url, { headers: h })
  }

  let response = await send(accessToken)
  if (response.status === 401 && tokenStorage.getRefreshToken()) {
    const newAccessToken = await refreshAccessToken()
    response = await send(newAccessToken)
  }

  if (!response.ok) {
    let errorMessage =
      response.status === 401
        ? (tokenStorage.getAccessToken()
            ? 'Sign in again to view this file.'
            : 'Sign in to view this file.')
        : response.status === 404
          ? 'File is not available in storage.'
          : `Could not load file (${response.status})`
    try {
      const errorData = await response.json()
      errorMessage = errorData.error || errorMessage
    } catch {
      // non-JSON error body
    }
    const err = new Error(errorMessage)
    err.status = response.status
    throw err
  }

  return as === 'text' ? response.text() : response.blob()
}

/** Ensure a valid access token before uploads (refreshes when a refresh token is available). */
export async function ensureFreshAccessToken() {
  if (!tokenStorage.getRefreshToken()) {
    const token = tokenStorage.getAccessToken()
    if (!token) {
      throw new Error('Session expired. Please log in again.')
    }
    return token
  }
  return refreshAccessToken()
}

async function fetchAPI(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`
  
  // Get access token and add to headers if available
  const accessToken = tokenStorage.getAccessToken()
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  }
  
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }

  const config = {
    ...options,
    headers,
  }

  try {
    let response = await fetch(url, config)
    
    // If 401 and we have a refresh token, try to refresh
    if (response.status === 401 && tokenStorage.getRefreshToken() && !endpoint.includes('/auth/refresh')) {
      try {
        const newAccessToken = await refreshAccessToken()
        // Retry the original request with new token (keep signal so callers can abort the retried fetch)
        headers['Authorization'] = `Bearer ${newAccessToken}`
        response = await fetch(url, {
          ...config,
          headers,
          ...(options.signal !== undefined ? { signal: options.signal } : {}),
        })
      } catch (refreshError) {
        // Refresh failed, clear tokens and throw
        tokenStorage.clearTokens()
        throw new Error('Session expired. Please log in again.')
      }
    }
    
    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`
      let code = null
      try {
        const errorData = await response.json()
        errorMessage = errorData.error || errorMessage
        code = errorData.code ?? null
      } catch (e) {
        errorMessage = response.statusText || errorMessage
      }
      const err = new Error(errorMessage)
      err.status = response.status
      err.code = code
      throw err
    }

    // 204 No Content has no body; response.json() would throw
    if (response.status === 204) {
      return null
    }

    return await response.json()
  } catch (error) {
    // Handle network errors (CORS, connection refused, etc.)
    if (error instanceof TypeError && error.message.includes('fetch')) {
      console.error('Network Error - Is the backend running?', error, { API_BASE_URL })
      throw new Error(
        `Failed to connect to API at ${API_BASE_URL}. Start the backend (e.g. cd backend && make run) and, in dev, set VITE_DEV_API_ORIGIN in frontend/.env.local if the API is not on port 8080.`
      )
    }
    console.error('API Error:', error)
    throw error
  }
}

/** Build GET path with query string; omits undefined, null, and empty string. */
function pathWithQuery(path, params = {}) {
  const q = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    q.append(key, String(value))
  }
  const s = q.toString()
  return s ? `${path}?${s}` : path
}

async function fetchPublicSettings() {
  return fetchAPI('/settings')
}

// Repository API
export const repositoryAPI = {
  // Get list of repositories with filters
  list: async (params = {}) => {
    const { signal, ...rest } = params
    const fetchOpts = signal ? { signal } : {}
    return fetchAPI(
      pathWithQuery('/repositories', {
        page: rest.page,
        page_size: rest.page_size,
        search: rest.search,
        manufacturer: rest.manufacturer,
        device_type: rest.device_type,
        form_factor: rest.form_factor,
        license: rest.license,
        visibility: rest.visibility,
        sort: rest.sort,
      }),
      fetchOpts
    )
  },

  // Get featured repositories
  featured: async (params = {}) => {
    const { signal } = params
    return fetchAPI('/repositories/featured', signal ? { signal } : {})
  },

  // Get repository by slug
  getBySlug: async (slug) => {
    return fetchAPI(`/repositories/${slug}`)
  },

  // Get branches for a repository
  getBranches: async (slug) => {
    return fetchAPI(`/repositories/${slug}/branches`)
  },

  // Get open pull requests for a repository
  getIssues: async (slug) => {
    return fetchAPI(`/repositories/${slug}/issues`)
  },

  getPullRequests: async (slug, params = {}) => {
    return fetchAPI(pathWithQuery(`/repositories/${slug}/pull-requests`, { limit: params.limit, offset: params.offset }))
  },

  // Get versions for a repository
  getVersions: async (slug) => {
    return fetchAPI(`/repositories/${slug}/versions`)
  },

  // Get files for a repository (optional: branch; default branch if omitted)
  getFiles: async (slug, params = {}) => {
    return fetchAPI(pathWithQuery(`/repositories/${slug}/files`, { branch: params.branch }))
  },

  // Get commit history for a repository (optional: branch, limit)
  getCommits: async (slug, params = {}) => {
    return fetchAPI(pathWithQuery(`/repositories/${slug}/commits`, { branch: params.branch, limit: params.limit }))
  },

  // Get filter options. Optional: { manufacturer_q, device_type_q, form_factor_q, license_q } to search (returns up to 50 matches each).
  getFilters: async (params = {}) => {
    return fetchAPI(
      pathWithQuery('/repositories/filters', {
        manufacturer_q: params.manufacturer_q,
        device_type_q: params.device_type_q,
        form_factor_q: params.form_factor_q,
        license_q: params.license_q,
      })
    )
  },

  // Bookmark a repository (protected)
  bookmark: async (slug) => {
    return fetchAPI(`/repositories/${slug}/bookmark`, {
      method: 'POST',
    })
  },

  // Unbookmark a repository (protected)
  unbookmark: async (slug) => {
    return fetchAPI(`/repositories/${slug}/bookmark`, {
      method: 'DELETE',
    })
  },

  // Check if repository is bookmarked (protected)
  checkBookmark: async (slug) => {
    return fetchAPI(`/repositories/${slug}/bookmark`)
  },

  // Roll back main by one commit (protected; repo owner or admin only)
  rollback: async (slug) => {
    return fetchAPI(`/repositories/${slug}/rollback`, {
      method: 'POST',
    })
  },

  // Stats and owners for a repository the current user manages.
  getManage: async (slug) => {
    return fetchAPI(`/repositories/${slug}/manage`)
  },

  // Add another user as an owner (protected; existing owners only).
  addOwner: async (slug, username) => {
    return fetchAPI(`/repositories/${slug}/owners`, {
      method: 'POST',
      body: JSON.stringify({ username }),
    })
  },

  // Remove an additional owner. The creator cannot be removed.
  removeOwner: async (slug, userId) => {
    return fetchAPI(`/repositories/${slug}/owners/${userId}`, {
      method: 'DELETE',
    })
  },

  // Remove own repository from public view (protected; owner only). Archives the repo (same as admin suspend).
  deleteOwn: async (slug) => {
    return fetchAPI(`/repositories/${slug}`, {
      method: 'DELETE',
    })
  },

  // Fork repository (protected; creates a copy owned by current user). options.title = optional custom name; default is "Original (fork)".
  fork: async (slug, options = {}) => {
    const title = options.title != null ? String(options.title).trim() : ''
    return fetchAPI(`/repositories/${slug}/fork`, {
      method: 'POST',
      body: JSON.stringify({ title }),
    })
  },

  // Get list of forks of a repository
  getForks: async (slug) => {
    return fetchAPI(`/repositories/${slug}/forks`)
  },

  // Get releases for a repository
  getReleases: async (slug, params = {}) => {
    return fetchAPI(pathWithQuery(`/repositories/${slug}/releases`, { limit: params.limit, offset: params.offset }))
  },

  // Create a release (protected; repo owner or admin only)
  createRelease: async (slug, data) => {
    return fetchAPI(`/repositories/${slug}/releases`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // Set or clear official release (protected; repo owner or admin only)
  setReleaseOfficial: async (slug, releaseId, isOfficial) => {
    return fetchAPI(`/repositories/${slug}/releases/${releaseId}`, {
      method: 'PATCH',
      body: JSON.stringify({ is_official: isOfficial }),
    })
  },
}

// Version API
export const versionAPI = {
  // Get version by ID
  get: async (id) => {
    return fetchAPI(`/versions/${id}`)
  },
}

// Health check
export const healthAPI = {
  check: async () => {
    return fetchAPI('/health')
  },
}

// Auth API
export const authAPI = {
  // Sign up a new user
  signup: async (email, username, password, policyAgreed = false) => {
    const data = await fetchAPI('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, username, password, policy_agreed: policyAgreed }),
    })
    // Store tokens
    tokenStorage.setTokens(data.access_token, data.refresh_token)
    return data
  },

  // Log in a user
  login: async (email, password) => {
    const data = await fetchAPI('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
    // Store tokens
    tokenStorage.setTokens(data.access_token, data.refresh_token)
    return data
  },

  // Log out the current user
  logout: async () => {
    try {
      await fetchAPI('/auth/logout', {
        method: 'POST',
      })
    } catch (error) {
      // Even if logout fails, clear tokens client-side
      console.error('Logout error:', error)
    } finally {
      tokenStorage.clearTokens()
    }
  },

  // Refresh the access token
  refreshToken: async (refreshToken) => {
    const data = await fetchAPI('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    })
    // Store new tokens
    tokenStorage.setTokens(data.access_token, data.refresh_token)
    return data
  },

  // Get current user
  getCurrentUser: async () => {
    return fetchAPI('/auth/me')
  },

  forgotPassword: async (email) => {
    return fetchAPI('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  },

  resetPassword: async (token, newPassword) => {
    return fetchAPI('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    })
  },

  verifyEmail: async (token) => {
    return fetchAPI('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
  },

  resendVerification: async () => {
    return fetchAPI('/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({}),
    })
  },

  changePassword: async (currentPassword, newPassword) => {
    return fetchAPI('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    })
  },

  requestEmailChange: async (currentPassword, newEmail) => {
    return fetchAPI('/auth/request-email-change', {
      method: 'POST',
      body: JSON.stringify({
        current_password: currentPassword,
        new_email: newEmail,
      }),
    })
  },

  confirmEmailChange: async (token) => {
    return fetchAPI('/auth/confirm-email-change', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
  },
}

// File API
export const fileAPI = {
  // Upload a file
  upload: async (file, onProgress, options = {}) => {
    const circuitNotes = options.circuitNotes === true
    const sourceFiles = options.sourceFiles === true
    const params = new URLSearchParams()
    if (circuitNotes) params.set('circuit_notes', '1')
    if (sourceFiles) params.set('source_files', '1')
    const query = params.toString()
    const url = `${API_BASE_URL}/files/upload${query ? `?${query}` : ''}`

    const formData = new FormData()
    formData.append('file', file)

    const sendUpload = (accessToken) =>
      new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        let settled = false
        const finish = (fn) => {
          if (settled) return
          settled = true
          fn()
        }

        if (onProgress) {
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              const percentComplete = (e.loaded / e.total) * 100
              onProgress(percentComplete)
            }
          })
        }

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText)
              finish(() => resolve(response))
            } catch (error) {
              finish(() => reject(new Error('Failed to parse response')))
            }
            return
          }
          if (xhr.status === 413) {
            finish(() =>
              reject(
                new Error(
                  'Upload rejected (413): a gateway blocked the request body. On NGINX Gateway Fabric set ClientSettingsPolicy body.maxSize on the Gateway; on ingress-nginx set proxy-body-size / client_max_body_size for /api.'
                )
              )
            )
            return
          }
          try {
            const errorData = JSON.parse(xhr.responseText)
            finish(() => {
              const err = new Error(errorData.error || `HTTP error! status: ${xhr.status}`)
              err.status = xhr.status
              err.code = errorData.code ?? null
              reject(err)
            })
          } catch (error) {
            finish(() => reject(new Error(`HTTP error! status: ${xhr.status}`)))
          }
        })

        xhr.addEventListener('error', () => {
          finish(() =>
            reject(
              new Error(
                'Network error during file upload (no HTTP response). If the Network tab shows 413, raise the gateway upload limit; otherwise check VPN, DNS, or TLS.'
              )
            )
          )
        })

        xhr.addEventListener('abort', () => {
          finish(() => reject(new Error('Upload cancelled')))
        })

        xhr.timeout = 300000
        xhr.addEventListener('timeout', () => {
          finish(() => reject(new Error('Upload timed out')))
        })

        xhr.open('POST', url)
        if (accessToken) {
          xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`)
        }
        xhr.send(formData)
      })

    let accessToken = tokenStorage.getAccessToken()
    try {
      return await sendUpload(accessToken)
    } catch (err) {
      if (err?.status === 401 && tokenStorage.getRefreshToken()) {
        accessToken = await refreshAccessToken()
        return await sendUpload(accessToken)
      }
      if (err?.status === 401) {
        tokenStorage.clearTokens()
        throw new Error('Session expired. Please log in again.')
      }
      throw err
    }
  },

  // Get file by ID
  get: async (id) => {
    return fetchAPI(`/files/${id}`)
  },

  // Public file URL (avatars). Do not put JWTs here: access tokens expire in 15m and leak into logs.
  getDownloadUrl: (id) => {
    return `${API_BASE_URL}/files/${id}`
  },

  getText: async (id) => fetchFileBody(id, 'text'),

  getBlob: async (id) => fetchFileBody(id, 'blob'),

  download: async (id, filename) => {
    const blob = await fetchFileBody(id, 'blob')
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename || 'download'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  },
}

// Contribution API
export const contributionAPI = {
  // Create a new contribution
  create: async (contributionData) => {
    return fetchAPI('/contributions', {
      method: 'POST',
      body: JSON.stringify(contributionData),
    })
  },

  // Get list of user's contributions
  list: async (params = {}) => {
    return fetchAPI(
      pathWithQuery('/contributions', {
        page: params.page,
        page_size: params.page_size,
        status: params.status,
      })
    )
  },

  // Pending contributions for repos the user owns (Pull requests section)
  getPendingForOwnedRepos: async (params = {}) => {
    return fetchAPI(
      pathWithQuery('/contributions/pending-for-owned-repos', {
        page: params.page,
        page_size: params.page_size,
      })
    )
  },

  // Get a contribution by ID
  get: async (id) => {
    return fetchAPI(`/contributions/${id}`)
  },

  // Update a contribution (draft only)
  update: async (id, contributionData) => {
    return fetchAPI(`/contributions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(contributionData),
    })
  },

  // Approve own contribution (for new_repository self-approval)
  approve: async (id) => {
    return fetchAPI(`/contributions/${id}/approve`, {
      method: 'POST',
    })
  },

  // Reject contribution (admin or repo owner for new_commit / circuit_notes / new_issue)
  reject: async (id, reason) => {
    return fetchAPI(`/contributions/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    })
  },

  // Close an approved issue (repo owner or admin)
  closeIssue: async (id) => {
    return fetchAPI(`/contributions/${id}/close`, {
      method: 'POST',
    })
  },
}

// Admin API
export const adminAPI = {
  // Get moderation queue (pending contributions)
  getQueue: async (params = {}) => {
    return fetchAPI(pathWithQuery('/admin/queue', { page: params.page, page_size: params.page_size }))
  },

  // Approve a contribution
  approveContribution: async (id, notes = null) => {
    return fetchAPI(`/admin/contributions/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    })
  },

  // Reject a contribution
  rejectContribution: async (id, reason) => {
    return fetchAPI(`/admin/contributions/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    })
  },

  // List repositories for content moderation / takedowns (admin only)
  getRepositories: async (params = {}) => {
    return fetchAPI(
      pathWithQuery('/admin/repositories', {
        archived: params.archived != null && params.archived !== '' ? params.archived : undefined,
        q: params.q != null && params.q !== '' ? params.q : undefined,
        limit: params.limit,
        offset: params.offset,
      })
    )
  },

  // Suspend for review (hide) or restore a repository (admin only). isArchived: true = suspend, false = restore
  setRepositoryArchived: async (repositoryId, isArchived) => {
    return fetchAPI(`/admin/repositories/${repositoryId}`, {
      method: 'PATCH',
      body: JSON.stringify({ is_archived: isArchived }),
    })
  },

  // Permanently delete a repository – takedown (admin only). Cannot be undone.
  deleteRepository: async (repositoryId) => {
    return fetchAPI(`/admin/repositories/${repositoryId}`, {
      method: 'DELETE',
    })
  },

  // List content reports (flags) for moderation queue (admin only)
  getReports: async (params = {}) => {
    return fetchAPI(
      pathWithQuery('/admin/reports', {
        status: params.status != null && params.status !== '' ? params.status : undefined,
        limit: params.limit,
        offset: params.offset,
      })
    )
  },

  // Dismiss a content report (admin only). Removes it from the pending queue.
  dismissReport: async (reportId) => {
    return fetchAPI(`/admin/reports/${reportId}`, {
      method: 'PATCH',
      body: JSON.stringify({ status: 'dismissed' }),
    })
  },

  // List users for admin management (admin only)
  getUsers: async (params = {}) => {
    return fetchAPI(
      pathWithQuery('/admin/users', {
        status: params.status != null && params.status !== '' ? params.status : undefined,
        q: params.q != null && params.q !== '' ? params.q : undefined,
        limit: params.limit,
        offset: params.offset,
      })
    )
  },

  // Update user status (suspend, ban, activate) (admin only)
  setUserStatus: async (userId, action) => {
    return fetchAPI(`/admin/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ action }),
    })
  },

  // Update founder/certification flags (admin only)
  updateUserFoundingFlags: async (userId, body) => {
    return fetchAPI(`/admin/users/${userId}/founding`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    })
  },

  // Get platform settings (same as public GET /api/settings; used by admin tab to load current values)
  getSettings: fetchPublicSettings,
  // Update platform settings (admin only)
  updateSettings: async (body) => {
    return fetchAPI('/admin/settings', {
      method: 'PATCH',
      body: JSON.stringify(body),
    })
  },

  // Tracked campaign links (admin only)
  getReferralLinks: async () => {
    return fetchAPI('/admin/referral-links')
  },

  createReferralLink: async (body) => {
    return fetchAPI('/admin/referral-links', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },

  updateReferralLink: async (id, body) => {
    return fetchAPI(`/admin/referral-links/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    })
  },

  deleteReferralLink: async (id) => {
    return fetchAPI(`/admin/referral-links/${id}`, {
      method: 'DELETE',
    })
  },
}

// Public tracked links. Recording a visit does not require an account.
export const referralAPI = {
  get: async (code) => {
    return fetchAPI(`/referrals/${encodeURIComponent(code)}`)
  },

  recordVisit: async (code, visitorId) => {
    return fetchAPI(`/referrals/${encodeURIComponent(code)}/visit`, {
      method: 'POST',
      body: JSON.stringify({ visitor_id: visitorId }),
    })
  },
}

// Public platform settings (no auth required) - for maintenance banner and registration-closed message
export const settingsAPI = {
  get: fetchPublicSettings,
}

// Founders program API (public)
export const foundersAPI = {
  get: async (params = {}) => {
    const { signal, ...rest } = params
    return fetchAPI(
      pathWithQuery('/founders', { sort: rest.sort, limit: rest.limit, offset: rest.offset }),
      signal ? { signal } : {}
    )
  },
}

// Reports API (authenticated users submit content flags)
export const reportAPI = {
  submit: async (body) => {
    return fetchAPI('/reports', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },
}

// User API
export const userAPI = {
  // Get user profile by ID (public endpoint)
  get: async (id, options = {}) => {
    const { signal } = options
    return fetchAPI(`/users/${id}`, signal ? { signal } : {})
  },

  // Update user profile (protected - own profile or admin)
  update: async (id, updates) => {
    return fetchAPI(`/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    })
  },

  // Follow a user (protected)
  follow: async (id) => {
    return fetchAPI(`/users/${id}/follow`, {
      method: 'POST',
    })
  },

  // Unfollow a user (protected)
  unfollow: async (id) => {
    return fetchAPI(`/users/${id}/follow`, {
      method: 'DELETE',
    })
  },

  // Get current user's bookmarks (protected)
  getBookmarks: async (page = 1, pageSize = 20) => {
    return fetchAPI(pathWithQuery('/users/me/bookmarks', { page, page_size: pageSize }))
  },

  // Search active users by username. Caller must manage the given repository.
  search: async (q, repositorySlug) => {
    return fetchAPI(pathWithQuery('/users/search', { q, repository: repositorySlug }))
  },

  // Repositories the current user owns or co-owns (for dashboard "repos you manage")
  getMyRepositories: async (page = 1, pageSize = 50) => {
    return fetchAPI(pathWithQuery('/users/me/repositories', { page, page_size: pageSize }))
  },
}

export default {
  repository: repositoryAPI,
  version: versionAPI,
  health: healthAPI,
  auth: authAPI,
  file: fileAPI,
  contribution: contributionAPI,
  user: userAPI,
}
